import {
    action,
    computed,
    observable,
    IReactionDisposer,
    autorun,
    set
} from 'mobx';
import { cloneDeep } from 'lodash';
import type { RootStore } from './root.store';
import { AutomationStep } from '../models/automations-steps/automation-step.model';
import { TAutomationSideBarScreen } from '../components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/automation-sidebar.types';
import { IAutomationEmailSettings, IVendorEmail } from '~/types/model.types';
import {
    IConfirmModalProps,
    AutomationBuilderState,
    TAutomationEmailEditorState
} from '../types/automation.types';
import {
    fixHTMLWithEmptyTableCells,
    processEmailForEdit
} from '../utils/emails.utils';

export class AutomationUiStore {
    rootStore: RootStore;

    @observable selectedAutomationId: string;
    @observable selectedWorkspaceId: string;
    @observable selectedStepId: string;
    @observable selectedAutomationSidebarScreen: TAutomationSideBarScreen;
    @observable activeHoverCreateBlockId: string;
    @observable activeExpandedCreateBlockId: string;
    @observable isShowErrors: boolean;
    @observable builderState: AutomationBuilderState;
    @observable titleRef: HTMLInputElement;
    @observable isTitleFocused: boolean;
    @observable private _customEmail: IVendorEmail;
    @observable private _customEmailSettings: IAutomationEmailSettings;
    @observable emailEditorState: TAutomationEmailEditorState = null;
    @observable isEmailEditorEditable: boolean;
    @observable isRequestInProgress: boolean;
    @observable confirmModalProps: IConfirmModalProps;
    @observable lastSelectedStepForAnimation: AutomationStep;
    @observable wasTemplateWarningModalShown: boolean;
    reactions: IReactionDisposer[];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.reset();
    }

    @computed
    get selectedAutomation() {
        return this.rootStore.automationStore.automations[
            this.selectedAutomationId
        ];
    }

    @computed
    get selectedWorkspace() {
        return this.rootStore.workspaceStore.workspaces[
            this.selectedWorkspaceId
        ];
    }

    @computed
    get selectedStep() {
        return (
            this.lastSelectedStepForAnimation ||
            this.selectedAutomation?.workflow_steps?.find(
                step => step.id === this.selectedStepId
            )
        );
    }

    @computed
    get isEditingDisabled() {
        return this.isApplied || this.isRequestInProgress;
    }

    @computed
    get isSelectedStepEditingDisabled() {
        return this.isEditingDisabled || this.selectedStep?.isDone;
    }

    @computed
    get hasSelectedStep() {
        return !!this.selectedStepId;
    }

    @computed
    get isCreate() {
        return this.builderState === AutomationBuilderState.Create;
    }

    @computed
    get isPreview() {
        return this.builderState === AutomationBuilderState.Preview;
    }

    @computed
    get isApplied() {
        return this.builderState === AutomationBuilderState.Applied;
    }

    @computed
    get isEdit() {
        return this.builderState === AutomationBuilderState.Edit;
    }

    @computed
    get customEmail(): IVendorEmail {
        const {
            userStore: { currUser }
        } = this.rootStore;

        if (!this._customEmail) {
            return null;
        }

        if (this.isCreate || this.isEmailEditorEditable) {
            return this._customEmail;
        }

        const transformedContent = processEmailForEdit({
            email: this._customEmail.html_body,
            emailSignature: currUser?.emailSignature
        });

        return {
            ...this._customEmail,
            html_body: transformedContent
        };
    }

    @computed
    get customEmailSettings(): IAutomationEmailSettings {
        if (!this._customEmailSettings) {
            return null;
        }

        if (this.isCreate || this.isEmailEditorEditable) {
            return this._customEmailSettings;
        }

        return null;
    }

    @computed
    get emailRecipient() {
        const {
            userStore: { currUser },
            companyStore: { currCompany }
        } = this.rootStore;
        const clients = this.selectedWorkspace.members.filter(
            member => member.system_user_type === 'client'
        );
        const emailRecipient = clients.length
            ? clients[0]
            : this.selectedWorkspace.members.find(
                  member =>
                      member._id !== currUser._id &&
                      member.company?._id !== currCompany._id
              );
        return emailRecipient;
    }

    @computed
    get tooltipCommonProps() {
        return {
            offsetX: 16,
            maxWidth: '220px',
            placement: 'left' as any,
            forceClose: !this.isApplied || this.selectedStep?.isDone
        };
    }

    @action.bound
    selectWorkspace(id: string) {
        this.selectedWorkspaceId = id;
    }

    @action.bound
    setShowErrors(showErrors: boolean) {
        this.isShowErrors = showErrors;
    }

    @action.bound
    selectAutomation(id?: string) {
        this.disposeReactions();
        this.selectedAutomationId = id;
        this.reactions.push(
            /* Side effect to reset the showErrors flag once the user
            has finished correcting all the errors */
            autorun(() => {
                if (this.isShowErrors && this.selectedAutomation?.isValid) {
                    this.setShowErrors(false);
                }
            }),
            // Side effect to prevent unloading when the automation is dirty
            autorun(() => {
                window.onbeforeunload = this.selectedAutomation?.isDirty
                    ? () => 'null'
                    : undefined;
            })
        );
    }

    @action.bound
    selectAutomationStep(id: string) {
        this.selectedStepId = id;
        this.clearLastSelectedStepForAnimation();
    }

    @action.bound
    clearLastSelectedStepForAnimation() {
        this.lastSelectedStepForAnimation = null;
        this.setSelectedAutomationSidebarScreen(null);
    }

    @action.bound
    deselectAutomationStep() {
        this.lastSelectedStepForAnimation = this.selectedStep;
        this.selectedStepId = null;
    }

    @action.bound
    setSelectedAutomationSidebarScreen(screen: TAutomationSideBarScreen) {
        this.selectedAutomationSidebarScreen = screen;
    }

    @action.bound
    setActiveHoverCreateBlockId(id: string) {
        this.activeHoverCreateBlockId = id;
    }

    @action.bound
    setExpandedCreateBlockId(id: string) {
        this.activeExpandedCreateBlockId = id;
    }

    @action.bound
    clearHoverAndCreateBlocks() {
        this.activeHoverCreateBlockId = null;
        this.activeExpandedCreateBlockId = null;
    }

    @action.bound
    setBuilderState(state: AutomationBuilderState) {
        this.builderState = state;
    }

    @action.bound
    setTitleRef(ref: HTMLInputElement) {
        this.titleRef = ref;
    }

    @action.bound
    setIsTitleFocused(isTitleFocused: boolean) {
        this.isTitleFocused = isTitleFocused;
    }

    @action.bound
    focusTitle() {
        this.titleRef.focus();
    }

    @action.bound
    setCustomEmail(email: Partial<IVendorEmail>) {
        if (!this._customEmail || !email) {
            this._customEmail = email as IVendorEmail;
        } else {
            set(this._customEmail, email);
        }

        if (this._customEmail) {
            this._customEmail.html_body = fixHTMLWithEmptyTableCells(
                this._customEmail.html_body
            );
        }
    }

    @action.bound
    setCustomEmailSettings(state: IAutomationEmailSettings) {
        this._customEmailSettings = state;
    }

    @action.bound
    setEmailEditorState(state: TAutomationEmailEditorState) {
        this.emailEditorState = state;
    }

    @action.bound
    setIsEmailEditorEditable(value: boolean) {
        this.isEmailEditorEditable = value;
    }

    @action.bound
    openEmailEditor(
        state: TAutomationEmailEditorState,
        template: IVendorEmail = {
            subject: '',
            html_body: '',
            title: '',
            file_attachments: [],
            image_attachments: []
        },
        settings?: IAutomationEmailSettings
    ) {
        this.setIsEmailEditorEditable(state === 'createNewTemplate');
        this.setEmailEditorState(state);
        this.setCustomEmail(cloneDeep(template));
        this.setCustomEmailSettings(cloneDeep(settings));
    }

    @action.bound
    closeEmailEditor() {
        this.setCustomEmail(null);
        this.setCustomEmailSettings(null);
        this.setEmailEditorState(null);
        this.setIsEmailEditorEditable(false);
    }

    @action.bound
    setIsRequestInProgress(isInProgress: boolean) {
        this.isRequestInProgress = isInProgress;
    }

    @action.bound
    setConfirmModalProps(props: IConfirmModalProps) {
        this.confirmModalProps = props;
    }

    @action.bound
    closeConfirmModal() {
        this.confirmModalProps = null;
    }

    @action.bound
    discardChangesToSelectedAutomation() {
        if (this.selectedAutomation?.isDirty) {
            if (
                !this.selectedAutomation.initValues.workflow_steps.includes(
                    this.selectedStep
                )
            ) {
                this.deselectAutomationStep();
            }
            this.rootStore.automationStore.load(
                this.selectedAutomation.initValues
            );
        }
    }

    @action.bound
    disposeReactions() {
        this.reactions?.forEach(dispose => dispose());
        this.reactions = [];
    }

    @action.bound
    setWasTemplateWarningModalShown(showModal: boolean) {
        this.wasTemplateWarningModalShown = showModal;
    }

    @action.bound
    reset() {
        this.selectedAutomationId = null;
        this.selectedWorkspaceId = null;
        this.selectedStepId = null;
        this.selectedAutomationSidebarScreen = null;
        this.activeHoverCreateBlockId = null;
        this.activeExpandedCreateBlockId = null;
        this.isShowErrors = false;
        this.builderState = null;
        this.titleRef = null;
        this.isTitleFocused = false;
        this._customEmail = null;
        this.emailEditorState = null;
        this.isEmailEditorEditable = false;
        this.isRequestInProgress = null;
        this.confirmModalProps = null;
        this.lastSelectedStepForAnimation = null;
        this.disposeReactions();
        this.wasTemplateWarningModalShown = false;
    }
}
